import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../../api";
import drIsInLogo from "../../assets/dr-is-in.png";
import "./ParentTeacherTipsPage.css";

type TipItem = {
  id?: number | string;
  title?: string;
  body?: string;
  image_url?: string | null;
  category?: string;
};

const PT_PORTAL_ROUTE = "/parent-teacher-portal";
const PT_TIPS_HOME_ROUTE = "/pt/tips";

function clean(v: any) {
  return String(v ?? "").trim();
}

function resolveUrl(raw?: string | null) {
  const v = clean(raw);
  if (!v) return "";
  if (v.startsWith("http://") || v.startsWith("https://")) return v;
  const base = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");
  return v.startsWith("/") ? `${base}${v}` : `${base}/${v}`;
}

function getPortalReturnPath(location: any): string {
  const fromState = clean(location?.state?.from);
  if (fromState) return fromState;

  const fromStore = clean(sessionStorage.getItem("pt_portal_return"));
  if (fromStore) return fromStore;

  return PT_PORTAL_ROUTE;
}

function prettyCategory(cat: string) {
  const c = clean(cat).toLowerCase();
  if (c === "physical") return "Physical Wellness";
  if (c === "mental") return "Mental Health & Stress";
  if (c === "everyday") return "Everyday Healthy Habits";
  return clean(cat) || "Tips";
}

function categoryHelpText(cat: string) {
  const c = clean(cat).toLowerCase();
  if (c === "physical") return "Simple body-based wellness tips for parents and teachers.";
  if (c === "mental") return "Helpful calming and reset tools for everyday stress.";
  if (c === "everyday") return "Small practical habits that support healthy routines.";
  return "Helpful wellness tips for parents and teachers.";
}

function normalizeTips(data: any): TipItem[] {
  const arr =
    (Array.isArray(data) && data) ||
    (Array.isArray(data?.tips) && data.tips) ||
    (Array.isArray(data?.items) && data.items) ||
    [];

  return arr.map((x: any) => ({
    id: x?.id,
    title: clean(x?.title),
    body: clean(x?.body || x?.content || x?.tip_text || x?.description),
    image_url: x?.image_url || x?.image || x?.photo_url || null,
    category: clean(x?.category),
  }));
}

export default function ParentTeacherTipsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { category = "" } = useParams();

  const [tips, setTips] = useState<TipItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const portalReturn = useMemo(() => getPortalReturnPath(location), [location]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErr("");

      try {
        const res = await api.get(`/tips/public?category=${encodeURIComponent(category)}`);
        setTips(normalizeTips(res.data));
      } catch (e: any) {
        const detail = e?.response?.data?.detail ?? e?.message ?? "Failed to load tips.";
        setErr(typeof detail === "string" ? detail : JSON.stringify(detail));
        setTips([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [category]);

  const handleBack = () => {
    navigate(PT_TIPS_HOME_ROUTE, { state: { from: portalReturn || PT_PORTAL_ROUTE } });
  };

  return (
    <div className="ptTipsPage-page">
      <div className="ptTipsPage-shell">
        <div className="ptTipsPage-top">
          <button className="ptTipsPage-back" type="button" onClick={handleBack}>
            ← Back
          </button>

          <div className="ptTipsPage-brand">
            <img src={drIsInLogo} className="ptTipsPage-logo" alt="Doctor Is In" />
            <div>
              <div className="ptTipsPage-title">{prettyCategory(category)}</div>
              <div className="ptTipsPage-sub">{categoryHelpText(category)}</div>
            </div>
          </div>
        </div>

        {loading ? <div className="ptTipsPage-notice">Loading...</div> : null}
        {err ? <div className="ptTipsPage-notice error">{err}</div> : null}

        <div className="ptTipsPage-grid">
          {!loading && !err && tips.length === 0 ? (
            <div className="ptTipsPage-notice">No tips found.</div>
          ) : (
            tips.map((tip, idx) => (
              <article key={String(tip.id ?? idx)} className="ptTipsPage-card">
                {tip.image_url ? (
                  <div className="ptTipsPage-imageWrap">
                    <img
                      src={resolveUrl(tip.image_url)}
                      alt={tip.title || "Tip"}
                      className="ptTipsPage-image"
                    />
                  </div>
                ) : null}

                <div className="ptTipsPage-cardBody">
                  <h3 className="ptTipsPage-cardTitle">{tip.title || "Tip"}</h3>
                  <p className="ptTipsPage-cardText">{tip.body || "—"}</p>
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </div>
  );
}